import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { ToastProvider } from "./components/ui/Toast";
import { NavigationProvider } from './contexts/NavigationContext';
import { ProtectedRoute } from './components/ProtectedRoute';

import Layout from './components/Layout';
import LoginForm from './LoginForm';

import OAuthCallback from './components/OAuthCallback';

import SkillsList from './skills/SkillsList';
import AddSkillPage from './skills/AddSkillPage';
import EditSkillPage from './skills/EditSkillPage';
import TestSkillPage from './skills/TestSkillPage';

import AgentsList from './agents/AgentsList';
import AddAgentPage from './agents/AddAgentPage';
import EditAgentPage from './agents/EditAgentPage';

import PackagesList from './packages/PackagesList';
import AddPackagePage from './packages/AddPackagePage';
import EditPackagePage from './packages/EditPackagePage';

import EditPromptPage from './prompts/EditPromptPage';

import { AuthProvider } from './contexts/AuthContext';

import CoreItemsPage from './admin/CoreItemsPage';
import AdminLanding from './admin/AdminLandingPage';
import UserManagementPage from './admin/UserManagementPage';

import AdaptersList from './adapters/AdaptersList';
import AddAdapterPage from './adapters/AddAdapterPage';
import EditAdapterPage from './adapters/EditAdapterPage';
import TestAdapterPage from './adapters/TestAdapterPage';

import AllExecutionsPage from './executions/AllExecutionsPage';
        
import NotFound from './components/NotFound';
import UnauthorizedPage from './components/Unauthorized';
import RootRedirect from './components/RootRedirect';
import MarketplaceRedirect from './components/MarketplaceRedirect';
import SessionExpiryHandler from './components/SessionExpiryHandler';

// Wrap the entity pages with entity-specific protection
interface ProtectedEntityPageProps {
  children: React.ReactNode;
  requireEdit?: boolean;
}

// Wrap the entity pages with entity-specific protection
const ProtectedSkillPage: React.FC<ProtectedEntityPageProps> = ({ 
  children, 
  requireEdit = false 
}) => (
  <ProtectedRoute entityType="skill">
    {children}
  </ProtectedRoute>
);

const ProtectedAgentPage: React.FC<ProtectedEntityPageProps> = ({ 
  children, 
  requireEdit = false 
}) => (
  <ProtectedRoute entityType="agent">
    {children}
  </ProtectedRoute>
);

const ProtectedPackagePage: React.FC<ProtectedEntityPageProps> = ({ 
  children, 
  requireEdit = false 
}) => (
  <ProtectedRoute entityType="package">
    {children}
  </ProtectedRoute>
);

// Wrap the Layout component with NavigationProvider and ProtectedRoute
const ProtectedLayout = () => (
  <ProtectedRoute>
    <NavigationProvider>
      <Layout />
    </NavigationProvider>
  </ProtectedRoute>
);

const AdminProtectedLayout = () => (
  <ProtectedRoute requireAdmin>
    <NavigationProvider>
      <Layout />
    </NavigationProvider>
  </ProtectedRoute>
);

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootRedirect />
  },
  {
    path: '/marketplace',
    element: <MarketplaceRedirect />
  },
  {
    path: '/login',
    element: <LoginForm />
  },
  {
    path: '/oauth/callback',
    element: <OAuthCallback />
  },
  {
    path: '/marketplace/unauthorized',
    element: <UnauthorizedPage />
  },
  {
    element: <ProtectedLayout />,
    children: [
      // Skills Routes
      {
        path: '/marketplace/skills',
        element: <SkillsList />
      },
      {
        path: '/marketplace/skills/add',
        element: <AddSkillPage />
      },
      {
        path: '/marketplace/skills/edit/:id',
        element: (
          <ProtectedSkillPage>
            <EditSkillPage />
          </ProtectedSkillPage>
        )
      },
      {
        path: '/marketplace/skills/test/:id',
        element: (
          <ProtectedSkillPage>
            <TestSkillPage />
          </ProtectedSkillPage>
        )
      },

      // Agents Routes
      {
        path: '/marketplace/agents',
        element: <AgentsList />
      },
      {
        path: '/marketplace/agents/add',
        element: <AddAgentPage />
      },
      {
        path: '/marketplace/agents/edit/:id',
        element: (
          <ProtectedAgentPage>
            <EditAgentPage />
          </ProtectedAgentPage>
        )
      },

      // Packages Routes
      {
        path: '/marketplace/packages',
        element: <PackagesList />
      },
      {
        path: '/marketplace/packages/add',
        element: <AddPackagePage />
      },
      {
        path: '/marketplace/packages/edit/:id',
        element: (
          <ProtectedPackagePage>
            <EditPackagePage />
          </ProtectedPackagePage>
        )
      },

      // Executions Routes
      {
        path: '/marketplace/executions',
        element: <AllExecutionsPage />
      },

      // Prompts Routes
      {
        path: '/marketplace/prompts/edit/:id',
        element: <EditPromptPage />
      },
      {
        // Unknown path
        path: '*',
        element: <NotFound />
      }
    ]
  },

  // Admin routes with additional protection
  {
    element: <AdminProtectedLayout />,
    children: [
      {
        path: '/marketplace/admin',
        element: <AdminLanding />
      },
      {
        path: '/marketplace/admin/core-items',
        element: <CoreItemsPage />
      },
      {
        path: '/marketplace/admin/users',
        element: <UserManagementPage />
      },
      {
        path: '/marketplace/admin/adapters',
        element: <AdaptersList />
      },
      {
        path: '/marketplace/admin/adapters/add',
        element: <AddAdapterPage />
      },
      {
        path: '/marketplace/admin/adapters/edit/:id',
        element: <EditAdapterPage />
      },
      {
        path: '/marketplace/admin/adapters/test/:id',
        element: <TestAdapterPage />
      }
    ]
  },
  {
    // Unknown path
    path: '*',
    element: <NotFound />
  }
]);

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <SessionExpiryHandler>
          <RouterProvider router={router} />
        </SessionExpiryHandler>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;